import type React from 'react';
import { useCallback } from 'react';
import noop from 'lodash/noop';
import {
	SELECTED_ISSUE_PARAM,
	SELECTED_COMMENT_PARAM,
} from '@atlassian/jira-business-constants/src/index.tsx';
import { useQueryParams } from '@atlassian/jira-software-router-utils/src/services/query-params/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createStore, createStateHook, createActionsHook } from '@atlassian/react-sweet-state';
import { localStorage, getSidebarWidthFromStore } from './utils.tsx';
import type { State } from './types.tsx';
import { STORAGE_KEY, SIDEBAR_MIN_WIDTH } from './constants.tsx';

export const getSidebarQueryParams = () => [SELECTED_ISSUE_PARAM, SELECTED_COMMENT_PARAM];

const setSidebarContent =
	(content: React.ReactElement | null, fullSizeMode = true) =>
	({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
		const state = getState();
		setState({
			...state,
			content,
			fullSizeMode,
		});
	};

const setSidebarWidth =
	(width: number) =>
	({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
		const state = getState();
		const sidebarWidthFromStore = getSidebarWidthFromStore(state);
		const minWidth = Math.min(sidebarWidthFromStore, SIDEBAR_MIN_WIDTH);

		const newWidth = Math.max(width, minWidth);
		if (newWidth === state.sidebarWidth) {
			return;
		}

		setState({
			...state,
			sidebarWidth: newWidth,
		});

		localStorage.set(STORAGE_KEY, newWidth);
	};

const setSidebarOnClose =
	(onSidebarClose: () => void) =>
	({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
		const state = getState();

		setState({
			...state,
			onSidebarClose,
		});
	};

const closeSidebar =
	() =>
	({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
		const state = getState();

		state.onSidebarClose();

		setState({
			...state,
			content: null,
			onSidebarClose: noop,
		});
	};

const actions = {
	setSidebarContent,
	setSidebarWidth,
	setSidebarOnClose,
	closeSidebar,
} as const;

const Store = createStore<State, typeof actions>({
	initialState: {
		content: null,
		fullSizeMode: false,
		sidebarWidth: null,
		onSidebarClose: noop,
	},
	actions,
	name: 'JWMSidebarStore',
});

export const useSidebarState = createStateHook(Store);
export const useSidebarActions = () => {
	const storeActions = createActionsHook(Store)();

	const sidebarQueryParams = getSidebarQueryParams();

	const [, setQueryParams] = useQueryParams(...sidebarQueryParams);

	const resetSidebarQueryParams = useCallback(() => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const newQueryParams = sidebarQueryParams.reduce<Record<string, any>>(
			(acc, param) => Object.assign(acc, { [param]: undefined }),
			{},
		);

		setQueryParams(newQueryParams);
	}, [setQueryParams, sidebarQueryParams]);

	return {
		...storeActions,
		resetSidebarQueryParams,
	};
};

export const useIsPanelOpenWithKey = createStateHook(Store, {
	selector: (state, key: string) => {
		if (!state.content) {
			return false;
		}

		return state.content.key === key;
	},
});
